import { RECRUITMENT_AI_STREAM_URL } from "common/Constants";

// One id per browser tab session — lets the backend's AI Chat usage log
// (WAX671O-5598) group a user's questions by session for auditing.
function getSessionId() {
  let id = window.sessionStorage.getItem("recruitmentAiSessionId");
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem("recruitmentAiSessionId", id);
  }
  return id;
}

// No EventSource usage exists anywhere in this codebase, and it can't do POST
// or custom headers anyway — this is a fetch + ReadableStream reader instead,
// parsing the same `data: {...}\n\n` SSE framing the MCP backend writes.
export async function streamRecruitmentChat({
  message,
  conversationHistory,
  currentContext,
  onEvent,
  signal,
}) {
  const resp = await fetch(RECRUITMENT_AI_STREAM_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Tymeplusjwt: window.localStorage.getItem("token") || "",
    },
    body: JSON.stringify({ message, conversationHistory, currentContext, sessionId: getSessionId() }),
    signal,
  });

  if (!resp.ok || !resp.body) {
    throw new Error(`Recruitment AI request failed (${resp.status})`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });

    let idx;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const raw = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      if (!raw.startsWith("data: ")) continue;
      try {
        onEvent(JSON.parse(raw.slice(6)));
      } catch (e) {
        console.error("[aiChatStream] failed to parse SSE frame:", raw, e);
      }
    }
  }
}

// One-shot AI actions (rank_candidates, draft_email, suggest_screening_question) —
// triggered from a button, not the chatbot. Same endpoint and framing as
// streamRecruitmentChat, but the backend writes a single SSE frame and ends, so
// this just waits for it and resolves with the parsed result.
export async function callRecruitmentAIAction(action, payload, onProgress) {
  const resp = await fetch(RECRUITMENT_AI_STREAM_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Tymeplusjwt: window.localStorage.getItem("token") || "",
    },
    body: JSON.stringify({ action, ...payload }),
  });

  if (!resp.ok || !resp.body) {
    throw new Error(`Recruitment AI action failed (${resp.status})`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });

    let idx;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const raw = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      if (!raw.startsWith("data: ")) continue;
      const parsed = JSON.parse(raw.slice(6));
      // Progress frames (e.g. rank_candidates_progress) are reported via
      // onProgress and the stream keeps going until the final result frame.
      if (parsed.type === `${action}_progress`) {
        onProgress?.(parsed);
        continue;
      }
      return parsed;
    }
  }
  throw new Error("Recruitment AI action returned no data");
}
